
import React, {useEffect, useState} from "react";
import "../loginPage.modules.css";
import Button from "../../../components/common/form/Button";
import {FaEnvelope, FaKey, FaPhone} from "react-icons/fa";
import InputField from "../../../components/common/form/InputField";
import {CUSTOMER_DISABLED_MESSAGE, useBaseUrlQuery, useLoginUserMutation} from "../loginPageApi";
import {handleRTKQuery} from "../../../helpers/utils/RtkQueryUtils";
import {useLocation, useNavigate} from "react-router-dom";
import {useRequestMfaChallengeMutation, useVerifyMfaChallengeMutation} from "../../profile/mfaDeviceApi";
import {showToast} from "../../../helpers/utils/Utils";
import {clearRedirectPath, setShowSidebar} from "../../../app/globalSlice/globalSlice";
import {useDispatch, useSelector} from "react-redux";
import ConfirmationModal from "../../../components/layout/modal/ConfirmationModal";
import CustomerAccessBanner from "../../../components/common/CustomerAccessBanner";

const VerifyMfaForm = () => {

    const navigate = useNavigate();

    const location = useLocation();
    const { username, password ,mfaResponse } = location.state || {};

    const [mfaDevices, setDevices] = useState<any>([]);
    const [selectedDevice, setSelectedDevice] = useState<any>([]);
    const [code, setCode] = useState<string>("");
    const [valid, setValid] = useState(false);
    const [isResendDisabled, setIsResendDisabled] = useState(true);
    const [timer, setTimer] = useState(30);
    const [isSubmit, setIsSubmit] = useState(false);
    const [requestMfaChallenge, {isLoading: requestMfaChallengeLoading}] = useRequestMfaChallengeMutation();
    const [verifyMfaChallenge, {isLoading: verifyMfaChallengeLoading}] = useVerifyMfaChallengeMutation();
    const [challengeId, setChallengeId] = useState(null);
    const [loginUser] = useLoginUserMutation();
    const redirectPath = useSelector((state: any) => state.global.redirectPath);
    const dispatch = useDispatch();
    const [showChangePasswordConfirmationModal, setShowChangePasswordConfirmationModal] = useState(false);
    const [showMigrationNotice, setShowMigrationNotice] = useState(false);
    const tenantData = useBaseUrlQuery(window.location.host);

    useEffect(() => {
        const storedResponse = mfaResponse;

        if (storedResponse != null) {

            const parsedResponse = JSON.parse(storedResponse);
            const challengeId = parsedResponse?.mfaInfo?.mfaChallengeId || null;

            setChallengeId(challengeId);

            const verifiedDevices = parsedResponse?.mfaInfo?.mfaDevices.filter(
                (el: any) => el.verified === true
            );

            const preferred = parsedResponse?.mfaInfo?.mfaDevices.filter(
                (el: any) => el.preferred
            );

            if (preferred.length > 0) {
                setSelectedDevice(preferred[0]);
            } else {
                setSelectedDevice(verifiedDevices[0]);
            }

            if (verifiedDevices && Array.isArray(verifiedDevices)) {
                setDevices(verifiedDevices); // Update the state with parsedResponse
            }
        }
    }, []);
    function showForgotPassword() {
        navigate("/forgot-password")
    }
    useEffect(() => {
        if (timer > 0 && isResendDisabled) {
            const countdown = setInterval(() => {
                setTimer(prev => prev - 1);
            }, 1000);
            return () => clearInterval(countdown);
        } else {
            setIsResendDisabled(false);
        }
    }, [timer, isResendDisabled]);

    const handleDeviceSelection = (deviceId: string) => {
        setSelectedDevice(deviceId);
    };

    useEffect(() => {
        if (isSubmit) {
            setIsResendDisabled(true);
            setTimer(30);
        }
    }, [isSubmit]);

    const handleSubmit = async (e: any) => {
        if (selectedDevice) {
            await handleRTKQuery(
                async () => {
                    return requestMfaChallenge({
                        challengeId: challengeId,
                        mfaDeviceId: selectedDevice?.id,
                        remember: true,
                    }).unwrap();
                },
                () => {
                    setIsSubmit(true);
                }
            );
        }
    };

    const startTimer = () => {
        setIsResendDisabled(true);
        setTimer(30);
        const timerInterval = setInterval(() => {
            setTimer((prevTimer) => {
                if (prevTimer <= 1) {
                    clearInterval(timerInterval); // Clear the interval when the timer ends
                    setIsResendDisabled(false); // Enable the resend button
                    return 0;
                }
                return prevTimer - 1;
            });
        }, 1000);
    };

    const handleResendCode = () => {
        if (!isResendDisabled) {
            startTimer();
            return requestMfaChallenge({
                challengeId: challengeId,
                mfaDeviceId: selectedDevice?.id,
                remember: true,
            }).unwrap();
        }
    };

    const showDisabledNotice = (response: any) => {
        if (response?.message !== CUSTOMER_DISABLED_MESSAGE) return false;
        localStorage.clear();
        setShowMigrationNotice(true);
        return true;
    };

    const completeLogin = async (mfaToken: string) => {
        try {
            const response = await loginUser({username, password, mfaToken}).unwrap();
            if (showDisabledNotice(response)) return;

            if (response?.errorCode === 200) {
                if (response.userForcePasswordResetStatus) {
                    setShowChangePasswordConfirmationModal(true);
                } else {
                    dispatch(setShowSidebar(false));
                    navigate(redirectPath);
                    dispatch(clearRedirectPath());
                }
            } else {
                showToast(response?.message || "Login failed", "error");
            }
        } catch (error: any) {
            // loginPageApi owns HTTP failure toasts; migration outcomes use the inline notice.
            showDisabledNotice(error?.data);
        }
    };

    const handleVerifySubmit = async (e: any) => {
        if (code) {
            setShowMigrationNotice(false);
            await handleRTKQuery(
                async () => {
                    return verifyMfaChallenge({
                        challengeId: challengeId,
                        solution: code,
                    }).unwrap();
                },
                async (response: any) => {
                    if (response?.token) {
                        await completeLogin(response.token);
                    } else {
                        showToast('Invalid verification code.', 'error');
                    }
                }
            );
        }
    };


    const getDeviceIcon = (obj: any) => {
        switch (obj?.type) {
            case "AUTHENTICATOR":
                return (
                    <div
                        className={`cursor-pointer flex gap-5 px-3 py-5 items-center mb-3 mt-3 rounded ${obj.id === selectedDevice?.id ? "bg-surface-7 text-text-0" : ""}`}
                        onClick={() => handleDeviceSelection(obj)}>
                        <FaKey size={40} className={"text-2xl text-highlight-0"}/>
                        <p className={`text-xl mb-0 ${obj.id === selectedDevice?.id ? "text-surface-4 " : ""}`}>Authenticator App</p>
                    </div>
                );
            case "EMAIL":
                return (
                    <div
                        className={`cursor-pointer flex gap-5 px-3 py-5 items-center mb-3 mt-3 rounded ${obj.id === selectedDevice?.id ? "bg-surface-7 text-text-0" : ""}`}
                        onClick={() => handleDeviceSelection(obj)}>
                        <FaEnvelope size={40} className={"text-2xl text-highlight-0"}/>
                        <p className={`text-xl mb-0 ${obj.id === selectedDevice?.id ? "text-surface-4 " : ""}`}>Email {obj.credential}</p>
                    </div>
                );
            case "SMS":
                return (
                    <div
                        className={`cursor-pointer flex gap-5 px-3 py-5 items-center mb-3 mt-3 rounded ${obj.id === selectedDevice?.id ? "bg-surface-7 text-text-0" : ""}`}
                        onClick={() => handleDeviceSelection(obj)}>
                        <FaPhone size={40} className={"text-2xl text-highlight-0"}/>
                        <p className={`text-xl mb-0 ${obj.id === selectedDevice?.id ? "text-surface-4 " : ""}`}>Phone {obj.credential}</p>
                    </div>
                );
            default:
                return null;
        }
    };
    const handleVerificationCodeChange = (e:any) => {
        const value = e.target.value;
        setCode(value);

        // Check if the value is a valid 6 numeric digits
        const isValid = /^\d{6}$/.test(value);
        setValid(isValid);
    };

    function getMessage(type: string) {
        let message;
        switch (type) {
            case 'AUTHENTICATOR':
                message = (
                    <>
                        <h6 className="text-center mb-3 text-text-7">A verification code has been sent to your
                            authenticator
                            app.</h6>
                        <p className="text-center text-text-7">Please enter the code generated by the app.</p>
                    </>
                );
                break;
            case 'SMS':
                message = (
                    <>
                        <h6 className="text-center mb-3 text-text-7">A verification code has been sent to your mobile
                            device via
                            SMS.</h6>
                        <p className="text-center text-text-7">Please enter the code received in the text message.</p>
                    </>
                );
                break;
            case 'EMAIL':
                message = (
                    <>
                        <h6 className="text-center mb-3 text-text-7">A verification code has been sent to your email
                            address.</h6>
                        <p className="text-center text-text-7">Please enter the code received in the email.</p>
                    </>
                );
                break;
            default:
                message = (
                    <>
                        <h6 className="text-center mb-3 text-text-7">A verification code has been sent to your selected
                            device.</h6>
                        <p className="text-center text-text-7">Please enter the code to proceed.</p>
                    </>
                );
        }

        return message;
    }
    return (
        <>
            <section className="login_form flex items-center justify-center ">
                <div
                    className={'min-h-[calc(100vh-48px)] relative flex-grow flex justify-center items-center before:absolute before:inset-0 before:bg-custom-image before:bg-center before:bg-no-repeat before:bg-cover before:content-[\'\'] before:filter before:blur-[2px] after:absolute after:inset-0 after:bg-[rgba(0,0,0,0.4)] after:content-[\'\']'}>
                    <div className="container mx-auto px-4">
                        <div className="flex justify-center">
                            <div className="w-full max-w-[635px] bg-text-0 relative z-10 p-8 rounded text-center">

                                        {showMigrationNotice && <div className="mb-4 text-left">
                                            <CustomerAccessBanner supportEmail={tenantData.data?.supportEmail} />
                                        </div>}

                                        {!isSubmit ?
                                            <>
                                                <h1 className={"mt-1 text-2xl font-semibold text-highlight-4 text-text-7 mb-5 pt-2"}>
                                                    Choose a way to Authenticate
                                                </h1>
                                                {mfaDevices.length > 0 ? (
                                                    mfaDevices.map((el: any, index: number) => (
                                                        <React.Fragment key={index}>
                                                            {getDeviceIcon(el)}
                                                        </React.Fragment>
                                                    ))
                                                ) : (
                                                    <h2 className={"mt-3 text-xl text-text-0"}>
                                                        No MFA devices available.
                                                    </h2>
                                                )}

                                                {mfaDevices.length > 0 &&
                                                    <div className="form-group mt-4">
                                                        <Button
                                                            btnText={"Send Verification Code"}
                                                            isLoading={requestMfaChallengeLoading}
                                                            onClick={handleSubmit}
                                                            type={"submit"}
                                                            isDisabled={!selectedDevice}
                                                        ></Button>
                                                    </div>
                                                }
                                            </>
                                            :
                                            <>
                                                <h1 className={"mt-1 text-2xl text-text-7 font-semibold mb-3"}>
                                                    Verify Your Identity
                                                </h1>
                                                {getMessage(selectedDevice?.type)}

                                                <InputField
                                                    name="code"
                                                    label=""
                                                    value={code}
                                                    onChange={(e) => handleVerificationCodeChange(e)}
                                                    type="email"
                                                    required
                                                    placeholder="Enter Code..."
                                                    hideLabel={true}
                                                    className="form-control"
                                                    maxLength={6}
                                                />

                                                <div className="form-group mt-4">
                                                    <Button
                                                        btnText={"Verify Code"}
                                                        isLoading={verifyMfaChallengeLoading}
                                                        onClick={handleVerifySubmit}
                                                        type={"submit"}
                                                        isDisabled={!valid}
                                                        className={'min-w-[120px] text-center justify-center'}
                                                    ></Button>
                                                </div>

                                                { selectedDevice?.type !== 'AUTHENTICATOR' && <div className="form-group mt-4">
                                                    <Button
                                                        btnText={isResendDisabled ? `Re-send (${timer}s)` : "Re-send"}
                                                        isLoading={false}
                                                        onClick={handleResendCode}
                                                        type={"submit"}
                                                        isDisabled={isResendDisabled}
                                                        className={'min-w-[120px] text-center justify-center'}
                                                    ></Button>
                                                </div>
                                                }
                                            </>
                                        }



                            </div>
                        </div>
                    </div>
                </div>

            </section>
            {showChangePasswordConfirmationModal && <ConfirmationModal title={"Alert"}
                                                                       bodyMessage={"Your password needs to be reset for security reasons. Please create a new password to continue."}
                                                                       onSubmit={showForgotPassword}
                                                                       show={showChangePasswordConfirmationModal}
                                                                       setShow={setShowChangePasswordConfirmationModal}
            />
            }
        </>
    );
};

export default VerifyMfaForm;
